import React, { useState } from 'react'
import { GitCompare } from 'lucide-react'
import { AnalyticsLayout } from '@/components/analytics/AnalyticsLayout'
import { ScorecardTable, ScorecardRow } from '@/components/analytics/ScorecardTable'
import { ScorecardCompareModal } from '@/components/analytics/ScorecardCompareModal'
import { ExportButton } from '@/components/shared/ExportButton'
import { ChartInfoButton } from '@/components/shared/ChartInfoButton'
import { useAnalyticsQuery } from '@/hooks/useAnalyticsQuery'
import { useAnalyticsFilters } from '@/contexts/AnalyticsFiltersContext'

interface ScorecardResponse {
  kind: 'broker'
  minProjects: number
  rows: ScorecardRow[]
}

function BrokerBody() {
  const { queryParams } = useAnalyticsFilters()
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [compareOpen, setCompareOpen] = useState(false)
  const { data, isLoading, error } = useAnalyticsQuery<ScorecardResponse>(
    'scorecards.brokers',
    '/analytics/scorecards/brokers',
  )

  const canCompare = selectedIds.length >= 2 && selectedIds.length <= 4

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-2">
          <div>
            <h2 className="text-lg font-semibold text-secondary-900">Broker scorecard</h2>
            <p className="text-xs text-secondary-500">
              Ranked by GMV across all brokers with at least one project in the filtered set.
              Select 2–4 brokers to compare.
            </p>
          </div>
          <ChartInfoButton
            title="Broker scorecard"
            info={{
              definition:
                'Per-broker performance ranked by GMV. Useful to identify which brokers are moving product and where your target segments are best represented.',
              formula:
                'GMV = SUM(current_price of sold units) · sell-through = sold / (sold + available + reserved) × 100 · median €/m² percentile = rank of broker\'s median €/m² vs the universe · pipeline value = SUM(current_price of available units) · sales 90d = COUNT(unit) WHERE sales_date > now() - 90d',
              calculation:
                'Honors the filter bar. Brokers with at least one project in the scope are included. Compare mode (check 2–4 rows) loads the same brokers\' weekly time series for sales, cumulative sell-through and avg €/m².',
              source: 'unit, project, broker',
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setCompareOpen(true)}
            disabled={!canCompare}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-secondary-300 bg-white text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title={
              canCompare
                ? `Compare ${selectedIds.length} selected brokers`
                : 'Select 2–4 brokers to enable compare mode'
            }
          >
            <GitCompare className="h-4 w-4" />
            Compare ({selectedIds.length})
          </button>
          <ExportButton
            endpoint="/analytics/export/scorecards/brokers.csv"
            params={queryParams}
            filename="analytics-scorecards-brokers"
          />
        </div>
      </div>
      <ScorecardTable
        kind="broker"
        rows={data?.rows}
        loading={isLoading}
        error={error}
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />
      {compareOpen && canCompare && (
        <ScorecardCompareModal
          kind="broker"
          ids={selectedIds}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </div>
  )
}

export function BrokerScorecards() {
  return (
    <AnalyticsLayout filterVariant="broker">
      <BrokerBody />
    </AnalyticsLayout>
  )
}
